package com.bulut.mecitogullari.core.export

import com.bulut.mecitogullari.core.export.ReportHtml.escape
import com.bulut.mecitogullari.core.export.ReportHtml.money
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.i18n.unitDisplayLabel
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.i18n.localizedCategory
import com.bulut.mecitogullari.core.util.formatDate
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.data.model.ApartmentUnit
import com.bulut.mecitogullari.data.model.CashTransaction
import com.bulut.mecitogullari.data.model.ConstructionStages
import com.bulut.mecitogullari.data.model.Project
import com.bulut.mecitogullari.data.model.TransactionType
import com.bulut.mecitogullari.data.model.UnitStatus
import kotlin.math.abs

/** Printable project summary: building, unit sales and project-linked entries. */
object ProjectReportHtml {

    fun build(
        project: Project,
        units: List<ApartmentUnit>,
        transactions: List<CashTransaction>,
        craftsmanNames: Map<String, String>,
    ): String {
        val s = Localization.strings
        return ReportHtml.page(
            title = "${project.name} - ${s.pdfProjectSummary}",
            body = buildString {
                append("<h1>${escape(project.name)}</h1>")
                append("<p class=\"subtitle\">${s.pdfProjectSummary} • ${formatDate(todayUtcMidnightMillis())}")
                if (project.location.isNotBlank()) append(" • ${escape(project.location)}")
                append("</p>")

                appendGeneralInfo(project)
                appendUnitSummary(units)
                appendSoldUnits(units, craftsmanNames)
                appendTransactions(transactions)
            },
        )
    }

    private fun StringBuilder.appendGeneralInfo(project: Project) {
        val s = Localization.strings
        append("<h2>${s.pdfGeneralInfo}</h2><table>")
        project.startDateEpochMillis?.let {
            append("<tr><td>${s.pdfStartDate}</td><td class=\"num\">${formatDate(it)}</td></tr>")
        }
        project.expectedEndEpochMillis?.let {
            append("<tr><td>${s.pdfEstimatedEnd}</td><td class=\"num\">${formatDate(it)}</td></tr>")
        }
        val blocks = project.effectiveBlocks
        if (blocks.size > 1) {
            append("<tr><td>${s.pdfBlocks}</td><td class=\"num\">${blocks.size}</td></tr>")
            blocks.forEach { block ->
                append(
                    "<tr><td class=\"muted\">${escape(block.name)} ${s.pdfBlock}</td>" +
                        "<td class=\"num muted\">" +
                        s.blockLayoutSummary.fillTemplate(
                            "floors" to block.floors.toString(),
                            "perFloor" to block.apartmentsPerFloor.toString(),
                        ) + "</td></tr>"
                )
            }
        } else {
            append(
                "<tr><td>${s.pdfBuilding}</td><td class=\"num\">" +
                    s.pdfBuildingTemplate.fillTemplate(
                        "floors" to blocks.first().floors.toString(),
                        "perFloor" to blocks.first().apartmentsPerFloor.toString(),
                    ) + "</td></tr>"
            )
        }
        append(
            "<tr><td>${s.pdfConstructionProgress}</td>" +
                "<td class=\"num\">%${project.progressPercent}</td></tr>"
        )
        val doneStages = ConstructionStages.all.filter { it.id in project.completedStages }
        if (doneStages.isNotEmpty()) {
            append(
                "<tr><td>${s.pdfCompletedStages}</td><td class=\"num muted\">" +
                    doneStages.joinToString(", ") { escape(it.label) } + "</td></tr>"
            )
        }
        if (project.notes.isNotBlank()) {
            append("<tr><td>${s.pdfNote}</td><td class=\"num muted\">${escape(project.notes)}</td></tr>")
        }
        append("</table>")
    }

    private fun StringBuilder.appendUnitSummary(units: List<ApartmentUnit>) {
        val s = Localization.strings
        val sold = units.filter { it.status == UnitStatus.SOLD }
        append("<h2>${s.pdfUnitStatus}</h2><table>")
        append("<tr><td>${s.pdfTotalUnits}</td><td class=\"num\">${units.size}</td></tr>")
        append("<tr><td>${s.pdfSold}</td><td class=\"num\">${sold.size}</td></tr>")
        append(
            "<tr><td>${s.pdfReserved}</td><td class=\"num\">" +
                "${units.count { it.status == UnitStatus.RESERVED }}</td></tr>"
        )
        append(
            "<tr><td>${s.pdfAvailable}</td><td class=\"num\">" +
                "${units.count { it.status == UnitStatus.AVAILABLE }}</td></tr>"
        )
        append(
            "<tr class=\"total\"><td>${s.pdfSalesTotal}</td><td class=\"num\">" +
                "${money(sold.sumOf { it.salePrice ?: 0.0 })}</td></tr>"
        )
        append("</table>")
    }

    private fun StringBuilder.appendSoldUnits(
        units: List<ApartmentUnit>,
        craftsmanNames: Map<String, String>,
    ) {
        val s = Localization.strings
        val sold = units.filter { it.status == UnitStatus.SOLD }
            .sortedWith(compareBy({ it.block }, { it.number }))
        if (sold.isEmpty()) return

        append("<h2>${s.pdfSoldUnits}</h2><table>")
        append(
            "<thead><tr><th>${s.pdfUnit}</th><th>${s.pdfBuyer}</th><th>${s.date}</th>" +
                "<th class=\"num\">${s.pdfPrice}</th>" +
                "<th class=\"num\">${s.pdfPaid}</th>" +
                "<th class=\"num\">${s.pdfRemaining}</th></tr></thead><tbody>"
        )
        sold.forEach { unit ->
            val isBarter = unit.barterCraftsmanId != null
            val buyer = if (isBarter) {
                "${craftsmanNames[unit.barterCraftsmanId] ?: unit.buyerName} (${s.pdfBarterSuffix})"
            } else {
                unit.buyerName.ifBlank { s.none }
            }
            append("<tr>")
            append("<td>${escape(unitDisplayLabel(unit.block, unit.number))}</td>")
            append("<td>${escape(buyer)}</td>")
            append("<td>${unit.saleDateEpochMillis?.let { formatDate(it) } ?: s.none}</td>")
            append("<td class=\"num\">${money(unit.salePrice ?: 0.0)}</td>")
            if (isBarter) {
                append("<td class=\"num muted\">${s.none}</td><td class=\"num muted\">${s.none}</td>")
            } else {
                append("<td class=\"num credit\">${money(unit.paidTotal)}</td>")
                append("<td class=\"num debit\">${money(unit.remaining)}</td>")
            }
            append("</tr>")
        }
        val cashSold = sold.filter { it.barterCraftsmanId == null }
        append("<tr class=\"total\">")
        append("<td colspan=\"3\">${s.total}</td>")
        append("<td class=\"num\">${money(sold.sumOf { it.salePrice ?: 0.0 })}</td>")
        append("<td class=\"num credit\">${money(cashSold.sumOf { it.paidTotal })}</td>")
        append("<td class=\"num debit\">${money(cashSold.sumOf { it.remaining })}</td>")
        append("</tr></tbody></table>")
    }

    private fun StringBuilder.appendTransactions(transactions: List<CashTransaction>) {
        val s = Localization.strings
        val income = transactions.filter { it.type == TransactionType.INCOME }.sumOf { it.amount }
        val expense = transactions.filter { it.type == TransactionType.EXPENSE }.sumOf { it.amount }

        if (transactions.isNotEmpty()) {
            append("<h2>${s.pdfProjectTransactions}</h2><table>")
            append(
                "<thead><tr><th>${s.date}</th><th>${s.description}</th><th>${s.category}</th>" +
                    "<th class=\"num\">${s.income}</th>" +
                    "<th class=\"num\">${s.expense}</th></tr></thead><tbody>"
            )
            transactions.sortedBy { it.dateEpochMillis }.forEach { tx ->
                val isIncome = tx.type == TransactionType.INCOME
                append("<tr>")
                append("<td>${formatDate(tx.dateEpochMillis)}</td>")
                append("<td>${escape(tx.description.ifBlank { s.none })}</td>")
                append("<td class=\"muted\">${escape(localizedCategory(tx.category))}</td>")
                append("<td class=\"num credit\">${if (isIncome) money(tx.amount) else ""}</td>")
                append("<td class=\"num debit\">${if (!isIncome) money(tx.amount) else ""}</td>")
                append("</tr>")
            }
            append("<tr class=\"total\">")
            append("<td colspan=\"3\">${s.total}</td>")
            append("<td class=\"num credit\">${money(income)}</td>")
            append("<td class=\"num debit\">${money(expense)}</td>")
            append("</tr></tbody></table>")
        }

        val net = income - expense
        append("<table class=\"summary\">")
        append("<tr><td>${s.pdfTotalIncome}</td><td class=\"num credit\">${money(income)}</td></tr>")
        append("<tr><td>${s.pdfTotalExpense}</td><td class=\"num debit\">−${money(expense)}</td></tr>")
        append(
            "<tr class=\"total\"><td>${s.pdfNet}</td><td class=\"num ${if (net < 0) "debit" else "credit"}\">" +
                "${if (net < 0) "−" else ""}${money(abs(net))}</td></tr>"
        )
        append("</table>")
    }

}
